import './ads-section-styte.scss'

import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { withTrans } from '@/i18n/withTrans'
import Box from '@/modules/components/Box'
import Button from '@/modules/components/Button'
import { useIsMobile } from '@/modules/components/hooks/useIsMobile'
import { useOneLink } from '@/modules/components/hooks/useOneLink'
import { useUserAgent } from '@/modules/components/hooks/useUserAgent'
import Input from '@/modules/components/Input'
import Link from '@/modules/components/Link'
import Typography from '@/modules/components/Typography'
import { validateEmail } from '@/utils/validate'

import appStoreBadge from './apple-playstore-outline.png'
import downloadApp from './Download-App.mp4'
import img from './download-app-loop-once.gif'
import { GeetestComponent } from './GeetestComponent'
import playStoreBadge from './google-playstore-outline.png'
import { submitEmail } from './utils/axios'

const SubmitButton = styled(Button)`
  position: absolute;
  width: 137px;
  height: 40px;
  top: 4px;
  right: 6px;
  padding: 14px 24px;
  color: #ffffff;
  font-weight: 600;
  font-size: 15px;
  line-height: 100%;
  letter-spacing: 0.015em;
  text-align: center;
  background-color: #061121;
  border: 0.5px solid #061121;
  border-radius: 37px;
`

const AdsSection = ({ t }) => {
  const { queryString } = useOneLink()
  const isMobile = useIsMobile()
  const [emailAddress, setEmailAddress] = useState('')
  const [isValid, setIsValid] = useState(true)
  const [isSent, setIsSent] = useState(false)
  const userAgent = useUserAgent()
  const isIos = userAgent === 'ios'
  const geetestRef = useRef(undefined)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isSent) {
        setIsSent(false)
        setEmailAddress('')
      }
    }, 8000)
    return () => clearTimeout(timer)
  }, [isSent])

  const handleInputEmail = (e) => {
    const { value } = e.target
    setEmailAddress(value)
    setIsValid(true)
  }

  const handleEmail = async (captchaValidateResult) => {
    try {
      await submitEmail({
        email: emailAddress,
        subscription: true,
        challenge: captchaValidateResult.geetest_challenge,
        seccode: captchaValidateResult.geetest_seccode,
      })
      setIsSent(true)
    } catch (err) {
      geetestRef.current?.reset()
      setIsValid(false)
    }
  }

  return (
    <section>
      <Box
        display="flex"
        width={'100%'}
        mb={'48px'}
        flexDirection={['column', 'row']}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Box
          display="flex"
          width={['100%', '377px', '50%']}
          flexDirection="column"
          alignItems={['center', 'flex-start']}
          justifyContent={'center'}
          customCss={{
            gap: '24px',
          }}
        >
          <Typography
            variant="plain"
            textAlign={['center', 'left']}
            fontSize={[32, 30, 36]}
            fontWeight={[700]}
            lineHeight={['125%']}
            color="#0B1426"
            maxWidth={[328, 377, 491]}
          >
            {t('ads.title')}
          </Typography>
          <Typography
            variant="plain"
            textAlign={['center', 'left']}
            fontSize={[16, 20]}
            fontWeight={[400]}
            lineHeight={['170%', '150%']}
            color="#0B1426"
            maxWidth={[328, 377, 476]}
          >
            {t('ads.subtitle')}
          </Typography>
          <Box width={[327, 381, 459]} position="relative">
            <Input
              type="email"
              variant="email"
              placeholder={t('ads.register.placeholder')}
              width={'100%'}
              onChange={handleInputEmail}
              value={emailAddress}
              flex={1}
            />
            <SubmitButton
              opacity={emailAddress.length ? 1 : 0.6}
              onClick={() => {
                setIsValid(validateEmail(emailAddress))
                if (validateEmail(emailAddress)) {
                  console.log('varified')
                  geetestRef.current?.verify()
                }
              }}
            >
              {t('ads.register.cta_button')}
            </SubmitButton>
            <Box mt={2} mb={'-8px'} maxWidth={['327px', '519px']}>
              {!isValid && (
                <Typography variant="adSectionSubscribeMsg" color="#D9475A">
                  {t('ads.subscription.error')}
                </Typography>
              )}
              {isSent && (
                <Typography variant="adSectionSubscribeMsg" color="#00A68C">
                  {t('ads.subscription.success')}
                </Typography>
              )}
            </Box>
            <Box>
              <GeetestComponent
                ref={geetestRef}
                onVerifySuccess={(data) => handleEmail(data)}
                geetestConfig={{
                  product: 'bind',
                }}
              />
            </Box>
          </Box>
          <Typography
            variant="plain"
            as="span"
            maxWidth={['327px', '381px', '504px']}
            fontWeight={500}
            fontSize={'12px'}
            lineHeight={'145%'}
            letterSpacing={'0.025em'}
            color={'#565F76'}
            dangerouslySetInnerHTML={{ __html: disclaimer }}
          />
          <Box>
            {isMobile && isIos && (
              <Link href={`https://crypto.onelink.me/RSfq${queryString}`}>
                <Box
                  as="img"
                  src={appStoreBadge}
                  alt="download crypto app"
                  width={130}
                  height={42}
                />
              </Link>
            )}
            {isMobile && !isIos && (
              <Link href={`https://crypto.onelink.me/veNW${queryString}`}>
                <Box
                  as="img"
                  src={playStoreBadge}
                  alt="download crypto app"
                  width={130}
                  height={42}
                />
              </Link>
            )}
          </Box>
        </Box>
        <Box
          position="relative"
          overflow={['hidden', 'unset']}
          width={[243]}
          height={[420, 462]}
          ml={['0px', '45px', '72px']}
        >
          <Box
            as="video"
            autoPlay
            muted
            alt="Get Crypto.com App"
            width={'100%'}
            height={[463, '100%']}
            mt={['63px', 0, 0]}
            position={'absolute'}
            display={['none', 'none', 'block']}
          >
            <source src={downloadApp} type="video/mp4" />
          </Box>
          <Box
            as="img"
            src={img}
            alt="Get Crypto.com App"
            width={'100%'}
            height={[463, '100%']}
            mt={['63px', 0, 0]}
            position={'absolute'}
            display={['block', 'block', 'none']}
          ></Box>
        </Box>
      </Box>
    </section>
  )
}
const radialRadientBg =
  'radial-gradient(100% 100% at 83% 31%, rgba(65, 148, 236, 0.24) 0%, rgba(65, 148, 236, 0) 100%), #061B38'

const disclaimer =
  "By clicking the Get Started button you acknowledge having read the <a target='_blank' href='https://crypto.com/privacy/global' style='text-decoration: underline'>Privacy Notice of Crypto.com</a> where we explain how we use and protect your personal data."

export default withTrans(AdsSection)
